import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
  TemplateRef,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { Modal } from '@spirit-testing/component-library';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import {
  CreatePatRequest,
  EXTRA_ACTION_DEPENDENCIES,
  PatAccessLevel,
  groupSelectablePatScopes,
  higherLevel,
  isLevelAtLeast,
  scopesForLevel,
  SELECTABLE_PAT_SCOPES,
} from '../../models/pat.model';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-pat-create-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    Modal,
    TranslatePipe,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatCheckboxModule,
    MatRadioModule,
    MatButtonModule,
    MatIconModule,
  ],
  templateUrl: './pat-create-dialog.component.html',
  styleUrl: './pat-create-dialog.component.scss',
})
export class PatCreateDialogComponent implements AfterViewInit {
  readonly visible = input.required<boolean>();
  readonly isBusy = input<boolean>(false);
  readonly createdToken = input<string | null>(null);

  readonly create = output<CreatePatRequest>();
  readonly visibleChange = output<boolean>();
  readonly closed = output<void>();

  @ViewChild('content') private contentTemplate!: TemplateRef<unknown>;

  private readonly fb = inject(NonNullableFormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly scopeGroups = groupSelectablePatScopes(SELECTABLE_PAT_SCOPES);

  private readonly levels = signal<Record<string, PatAccessLevel>>(this.initialLevels());
  private readonly extraScopes = signal<Set<string>>(new Set());

  readonly selectedScopes = computed<string[]>(() => {
    const levels = this.levels();
    const extras = this.extraScopes();
    const scopes: string[] = [];
    for (const group of this.scopeGroups) {
      scopes.push(...scopesForLevel(group.resource, levels[group.resource] ?? 'none'));
      for (const action of group.extraActions) {
        const scope = `${group.resource}:${action}`;
        if (extras.has(scope)) scopes.push(scope);
      }
    }
    return scopes;
  });

  readonly copied = signal(false);
  readonly curlCopied = signal(false);

  readonly quickstartUrl = environment.patQuickstartUrl;
  readonly swaggerUrl = environment.swaggerUrl;

  /** Copy-paste-ready curl call that authenticates the freshly created token against a read endpoint. */
  readonly curlExample = computed<string>(() => {
    const token = this.createdToken();
    if (!token) return '';
    return `curl -H "Authorization: Bearer ${token}" ${environment.apiBaseUrl}/api/v1/projects`;
  });

  readonly form = this.fb.group({
    name: this.fb.control('', Validators.required),
    expiresAt: this.fb.control<Date | null>(null),
  });

  readonly minExpiryDate = (() => {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    return date;
  })();

  resolvedTemplate: TemplateRef<unknown> | null = null;

  ngAfterViewInit(): void {
    this.resolvedTemplate = this.contentTemplate;
    this.cdr.detectChanges();
  }

  get createDisabled(): boolean {
    return this.form.invalid || this.selectedScopes().length === 0 || this.isBusy();
  }

  levelOf(resource: string): PatAccessLevel {
    return this.levels()[resource] ?? 'none';
  }

  /** Minimum level `resource` must keep while a checked extra action depends on it. */
  minLevelFor(resource: string): PatAccessLevel {
    let min: PatAccessLevel = 'none';
    for (const scope of this.extraScopes()) {
      const dependency = EXTRA_ACTION_DEPENDENCIES[scope];
      if (dependency?.resource === resource) min = higherLevel(min, dependency.level);
    }
    return min;
  }

  levelOptionDisabled(resource: string, level: PatAccessLevel): boolean {
    return !isLevelAtLeast(level, this.minLevelFor(resource));
  }

  setLevel(resource: string, level: PatAccessLevel): void {
    const min = this.minLevelFor(resource);
    const applied = isLevelAtLeast(level, min) ? level : min;
    this.levels.update((current) => ({ ...current, [resource]: applied }));
  }

  isExtraSelected(scope: string): boolean {
    return this.extraScopes().has(scope);
  }

  toggleExtra(scope: string, checked: boolean): void {
    this.extraScopes.update((current) => {
      const next = new Set(current);
      if (checked) {
        next.add(scope);
      } else {
        next.delete(scope);
      }
      return next;
    });

    const dependency = EXTRA_ACTION_DEPENDENCIES[scope];
    if (checked && dependency) {
      this.levels.update((current) => ({
        ...current,
        [dependency.resource]: higherLevel(current[dependency.resource] ?? 'none', dependency.level),
      }));
    }
  }

  onCreate(): void {
    if (this.createDisabled) return;
    const expiresAt = this.form.controls.expiresAt.value;
    this.create.emit({
      name: this.form.controls.name.value.trim(),
      scopes: this.selectedScopes(),
      expiresAt: expiresAt ? expiresAt.toISOString() : null,
    });
  }

  copyToken(): void {
    this.copyToClipboard(this.createdToken(), this.copied);
  }

  copyCurl(): void {
    this.copyToClipboard(this.curlExample(), this.curlCopied);
  }

  private copyToClipboard(text: string | null, copiedFlag: WritableSignal<boolean>): void {
    if (!text || !navigator.clipboard) return;
    navigator.clipboard.writeText(text).then(
      () => copiedFlag.set(true),
      () => {},
    );
  }

  onClose(): void {
    this.resetForm();
    this.visibleChange.emit(false);
    this.closed.emit();
  }

  onVisibleChange(visible: boolean): void {
    if (!visible) {
      this.resetForm();
      this.closed.emit();
    }
    this.visibleChange.emit(visible);
  }

  private resetForm(): void {
    this.form.reset({ name: '', expiresAt: null });
    this.levels.set(this.initialLevels());
    this.extraScopes.set(new Set());
    this.copied.set(false);
    this.curlCopied.set(false);
  }

  private initialLevels(): Record<string, PatAccessLevel> {
    return Object.fromEntries(this.scopeGroups.map((group) => [group.resource, 'none' as PatAccessLevel]));
  }
}
